import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  createDeskPlace,
  CreateDeskPlaceParams,
  createParkingPlace,
  CreateParkingPlaceParams,
  createRoomPlace,
  CreateRoomPlaceParams,
  deleteDeskPlace,
  deleteParkingPlace,
  deleteRoomPlace,
  PlaceFull,
  readAllPlacesAtPlan,
  readDeskPlace,
  readParkingPlace,
  readRoomPlace,
  updateDeskPlace,
  UpdateDeskPlaceParams,
  updateParkingPlace,
  UpdateParkingPlaceParams,
  updateRoomPlace,
  UpdateRoomPlaceParams,
} from 'src/api/places';
import {
  BackgroundData,
  getPlanBackground,
  savePlanUnderlay,
} from 'src/api/plan';
import { StatusResponse } from 'src/api/types';
import { toBookingObjectType } from 'src/pages/AdministratorPage/utils';
import {
  BookingObject,
  Desk,
  Parking,
  PlaceType,
  Room,
  RoomType,
} from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { toBookingObject } from 'src/utils/toBookingObject';

import { messagesActions } from '../messages/slice';
import { sidesheetActions } from '../sidesheet/slice';
import { StoreState, ThunkConfig } from '../types';

import { getAdminState } from './selectors';
import { adminActions } from './slice';

export interface AdminAllData {
  backgroundData: BackgroundData | undefined;
  bookingObjects: Map<string, BookingObject>;
}

export const fetchAdminAllData = createAsyncThunk<
  AdminAllData,
  number,
  ThunkConfig<string>
>('admin/fetchAdminAllData', async (floorId, { dispatch, rejectWithValue }) => {
  try {
    const [places, backgroundData] = await Promise.all([
      readAllPlacesAtPlan(floorId),
      getPlanBackground(floorId),
    ]);
    const bookingObjects: Map<string, BookingObject> = new Map();

    for (const place of places.places) {
      const bookingObject = toBookingObject(place);
      bookingObjects.set(bookingObject.mapPlaceId, bookingObject);
    }

    return { bookingObjects, backgroundData };
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});

export const fetchBookingObjects = createAsyncThunk<
  Map<string, BookingObject>,
  number,
  ThunkConfig<string>
>(
  'admin/fetchBookingObjects',
  async (floorId, { dispatch, rejectWithValue }) => {
    try {
      const places = await readAllPlacesAtPlan(floorId);
      const bookingObjects: Map<string, BookingObject> = new Map();

      for (const place of places.places) {
        const bookingObject = toBookingObject(place);
        bookingObjects.set(bookingObject.mapPlaceId, bookingObject);
      }

      return bookingObjects;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export interface FetchBookingObjectParams {
  id: number;
  type: PlaceType;
}

export const fetchBookingObject = createAsyncThunk<
  PlaceFull,
  FetchBookingObjectParams,
  ThunkConfig<string>
>(
  'admin/fetchBookingObject',
  async ({ id, type }, { dispatch, rejectWithValue }) => {
    try {
      switch (type) {
        case PlaceType.Desk:
          return readDeskPlace(id);
        case PlaceType.Room:
          return readRoomPlace(id);
        case PlaceType.Parking:
          return readParkingPlace(id);
      }
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export interface ChangePlaceParams {
  floorId: number;
  bookingObject: BookingObject;
}

const toGeometry = (bookingObject: BookingObject): string =>
  JSON.stringify({
    x: bookingObject.x,
    y: bookingObject.y,
    width: bookingObject.width,
    height: bookingObject.height,
    rotation: 'rotation' in bookingObject ? bookingObject.rotation : undefined,
    fontSize: bookingObject.fontSize,
  });

const toCreateDeskPlaceParams = (
  floorId: number,
  desk: Desk,
): CreateDeskPlaceParams => ({
  floorId,
  planGeometry: toGeometry(desk),
  title: desk.title ?? '',
  byWindow: desk.byWindow ?? false,
  withMonitor: desk.withMonitor ?? false,
  withCharger: desk.withCharger ?? false,
  withDivider: desk.withDivider ?? false,
  responsible: desk.responsible?.employeeNumber,
});

const toCreateRoomPlaceParams = (
  floorId: number,
  room: Room,
): CreateRoomPlaceParams => ({
  floorId,
  planGeometry: toGeometry(room),
  title: room.title ?? '',
  roomTypeId: room.roomType ?? RoomType.Meeting,
  square: room.square ?? 0,
  seatCount: room.seatCount ?? 0,
  withTv: room.withTv ?? false,
  withVideoCalls: room.withVideoCalls ?? false,
  withFlipChart: room.withFlipChart ?? false,
  withWater: room.withWater ?? false,
  adEmail: room.adEmail ?? '',
});

const toCreateParkingPlaceParams = (
  floorId: number,
  parking: Parking,
): CreateParkingPlaceParams => ({
  floorId,
  planGeometry: toGeometry(parking),
  title: parking.title ?? '',
  responsible: parking.guest ? undefined : parking.responsible?.employeeNumber,
  guest: parking.guest,
});

const toUpdateDeskPlaceParams = (
  placeId: number,
  desk: Desk,
): UpdateDeskPlaceParams => ({
  placeId,
  planGeometry: toGeometry(desk),
  title: desk.title ?? '',
  byWindow: desk.byWindow ?? false,
  withMonitor: desk.withMonitor ?? false,
  withCharger: desk.withCharger ?? false,
  withDivider: desk.withDivider ?? false,
  responsible: desk.responsible?.employeeNumber,
});

const toUpdateRoomPlaceParams = (
  placeId: number,
  room: Room,
): UpdateRoomPlaceParams => ({
  placeId,
  planGeometry: toGeometry(room),
  title: room.title ?? '',
  roomTypeId: room.roomType ?? RoomType.Meeting,
  seatCount: room.seatCount,
  withTv: room.withTv ?? false,
  withVideoCalls: room.withVideoCalls ?? false,
  withFlipChart: room.withFlipChart ?? false,
  withWater: room.withWater ?? false,
  adEmail: room.adEmail ?? '',
});

const toUpdateParkingPlaceParams = (
  placeId: number,
  parking: Parking,
): UpdateParkingPlaceParams => ({
  placeId,
  planGeometry: toGeometry(parking),
  title: parking.title ?? '',
  responsible: parking.guest ? undefined : parking.responsible?.employeeNumber,
  guest: parking.guest,
});

export const createPlace = createAsyncThunk<
  void,
  ChangePlaceParams,
  ThunkConfig<string>
>(
  'admin/createPlace',
  async ({ floorId, bookingObject }, { dispatch, rejectWithValue }) => {
    try {
      let result: StatusResponse;

      switch (bookingObject.type) {
        case PlaceType.Desk:
          result = await createDeskPlace(
            toCreateDeskPlaceParams(floorId, bookingObject),
          );
          break;
        case PlaceType.Room:
          result = await createRoomPlace(
            toCreateRoomPlaceParams(floorId, bookingObject),
          );
          break;
        case PlaceType.Parking:
          result = await createParkingPlace(
            toCreateParkingPlaceParams(floorId, bookingObject),
          );
          break;
      }

      if (!result.success)
        throw new Error(
          result.error ?? result.errorMessage ?? defaultServerError,
        );

      dispatch(
        messagesActions.show({
          message: `Объект бронирования ${bookingObject.title} успешно добавлен!`,
        }),
      );
      dispatch(fetchBookingObjects(floorId));
      dispatch(sidesheetActions.close());
    } catch (e) {
      const message = (e as AxiosError).message;

      dispatch(messagesActions.show({ message }));
      return rejectWithValue(message);
    }
  },
);

export const updatePlace = createAsyncThunk<
  void,
  ChangePlaceParams,
  ThunkConfig<string>
>(
  'admin/updatePlace',
  async ({ floorId, bookingObject }, { dispatch, rejectWithValue }) => {
    try {
      if (!bookingObject.id) return;

      let result: StatusResponse;

      switch (bookingObject.type) {
        case PlaceType.Desk:
          result = await updateDeskPlace(
            toUpdateDeskPlaceParams(bookingObject.id, bookingObject),
          );
          break;
        case PlaceType.Room:
          result = await updateRoomPlace(
            toUpdateRoomPlaceParams(bookingObject.id, bookingObject),
          );
          break;
        case PlaceType.Parking:
          result = await updateParkingPlace(
            toUpdateParkingPlaceParams(bookingObject.id, bookingObject),
          );
          break;
      }

      if (!result.success)
        throw new Error(
          result.error ?? result.errorMessage ?? defaultServerError,
        );

      dispatch(
        messagesActions.show({
          message: `Объект бронирования ${bookingObject.title} успешно обновлен!`,
        }),
      );
      dispatch(fetchBookingObjects(floorId));
      dispatch(sidesheetActions.close());
    } catch (e) {
      const message = (e as AxiosError).message;

      dispatch(messagesActions.show({ message }));
      return rejectWithValue(message);
    }
  },
);

export const savePlace = createAsyncThunk<void, void, ThunkConfig<string>>(
  'admin/savePlace',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { selectedPlace, floorId } = getAdminState(
        getState() as StoreState,
      );

      if (!selectedPlace || !floorId) return;

      const { width, height, fontSize } = selectedPlace;
      const rotation =
        'rotation' in selectedPlace ? selectedPlace.rotation : undefined;

      const roomType =
        selectedPlace.type === PlaceType.Room ? selectedPlace.roomType : null;

      if (selectedPlace.id) {
        await dispatch(
          updatePlace({
            floorId,
            bookingObject: selectedPlace,
          }),
        );
      } else {
        await dispatch(
          createPlace({
            floorId,
            bookingObject: selectedPlace,
          }),
        );
      }
      await dispatch(
        adminActions.setParamsByType({
          placeType: toBookingObjectType(selectedPlace.type, roomType),
          rotation,
          width,
          height,
          fontSize,
        }),
      );
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const deletePlace = createAsyncThunk<void, void, ThunkConfig<string>>(
  'admin/deletePlace',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { selectedPlace, floorId } = getAdminState(
        getState() as StoreState,
      );

      if (!selectedPlace?.id || !floorId) return;

      let result: StatusResponse;

      switch (selectedPlace.type) {
        case PlaceType.Desk:
          result = await deleteDeskPlace(selectedPlace.id);
          break;
        case PlaceType.Room:
          result = await deleteRoomPlace(selectedPlace.id);
          break;
        case PlaceType.Parking:
          result = await deleteParkingPlace(selectedPlace.id);
          break;
      }

      if (!result.success)
        throw new Error(
          result.error ?? result.errorMessage ?? defaultServerError,
        );

      await dispatch(sidesheetActions.close());
      await dispatch(fetchBookingObjects(floorId));
      dispatch(
        messagesActions.show({
          message: `Объект бронирования ${selectedPlace.title} успешно удален!`,
        }),
      );
    } catch (e) {
      const message = (e as AxiosError).message;

      dispatch(messagesActions.show({ message }));
      return rejectWithValue(message);
    }
  },
);

export const uploadPlan = createAsyncThunk<void, File, ThunkConfig<string>>(
  'admin/uploadPlan',
  async (file, { getState, dispatch, rejectWithValue }) => {
    try {
      const { floorId } = getAdminState(getState() as StoreState);

      if (!floorId) return;

      await savePlanUnderlay(floorId, file);
      await dispatch(fetchAdminAllData(floorId));
    } catch (e) {
      const error = e as AxiosError;
      const message =
        error.status === 413
          ? 'Размер файла слишком большой'
          : (error.message ?? defaultServerError);

      dispatch(
        messagesActions.show({
          message,
        }),
      );
      return rejectWithValue(message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { floorId } = getAdminState(getState() as StoreState);

      if (!floorId) return false;
    },
  },
);
